import React from 'react';

import { FontAwesomeIcon, Text } from '@schoolinks/ui-components';
import { isFunction } from 'lodash';
import { bool, func, string } from 'prop-types';
import { useCookies } from 'react-cookie';

import { useTranslation } from 'react-i18next';

import { faLanguage } from '@fortawesome/pro-regular-svg-icons/faLanguage';

import { MenuItem } from '@mui/material';

import {
  GOOGLE_TRANSLATE_COOKIE_KEY,
  GOOGLE_TRANSLATE_COOKIE_DOMAINS,
  DEFAULT_LANGUAGE_ENGLISH,
  GOOGLE_TRANSLATE_COOKIE_OPTIONS,
  I18N_TRANSLATE_COOKIE_KEY,
  DEFAULT_ENGLISH_GOOGLE_VALUE,
} from 'components/Common/GoogleTranslateV2/constants';
import Api from 'core/api';
import useMediaInfo from 'hooks/useMediaInfo';
import useMutation from 'hooks/useMutation';

const shouldShowGoogleTranslate = (reduxState, cookieState) =>
  !!reduxState || !!cookieState;

const LanguageTranslationMenuItem = ({
  className,
  onClickCallback,
  showGoogleTranslate,
  showIcon,
}) => {
  const { isMobile, isDesktop } = useMediaInfo();
  const { i18n } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [cookies, setCookie, removeCookie] = useCookies();
  const showGoogleTranslateDropdown = shouldShowGoogleTranslate(
    showGoogleTranslate,
    cookies[GOOGLE_TRANSLATE_COOKIE_KEY],
  );

  const { mutateAsync: setUserLanguage } = useMutation({
    apiFunc: Api.slUsers.updateUserDetails,
  });

  const handleToggleLanguageTranslation = async () => {
    if (isFunction(onClickCallback)) {
      onClickCallback();
    }

    // Only clear out cookies on desktop. Mobile/tablet handle this via language selection drawer
    if (!isDesktop) {
      return;
    }
    if (showGoogleTranslateDropdown) {
      // Remove all googtrans cookies from all domains and paths
      GOOGLE_TRANSLATE_COOKIE_DOMAINS.forEach(domain => {
        // First try with standard options
        removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
          ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
          domain,
        });
        removeCookie(I18N_TRANSLATE_COOKIE_KEY, {
          ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
          domain,
        });

        // Also try without expiration
        removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
          path: '/',
          domain,
        });
        removeCookie(I18N_TRANSLATE_COOKIE_KEY, {
          path: '/',
          domain,
        });
      });

      // Also try removing without domain)
      removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
        ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      });

      removeCookie(I18N_TRANSLATE_COOKIE_KEY, {
        ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      });

      await i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
      await setUserLanguage({
        payload: {
          activeLanguage: DEFAULT_LANGUAGE_ENGLISH,
        },
      });
    } else {
      await i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
      await setUserLanguage({
        payload: {
          activeLanguage: DEFAULT_LANGUAGE_ENGLISH,
        },
      });
    }
    const isI18NLanguageSelected =
      cookies[I18N_TRANSLATE_COOKIE_KEY] &&
      cookies[I18N_TRANSLATE_COOKIE_KEY] !== DEFAULT_LANGUAGE_ENGLISH;
    const isGoogleLanguageSelected =
      cookies[GOOGLE_TRANSLATE_COOKIE_KEY] &&
      cookies[GOOGLE_TRANSLATE_COOKIE_KEY] !== DEFAULT_ENGLISH_GOOGLE_VALUE;
    if (isI18NLanguageSelected || isGoogleLanguageSelected) {
      window.location.reload();
    }
  };
  const isIconVisible = isMobile || showIcon;

  return (
    <MenuItem className={className} onClick={handleToggleLanguageTranslation}>
      {isIconVisible && <FontAwesomeIcon color="grayDark" icon={faLanguage} />}
      <Text variant="bodySm400" ml={isIconVisible ? 1 : 0}>
        {showGoogleTranslateDropdown && isDesktop
          ? 'Hide Language Translation'
          : 'Language Translation'}
      </Text>
    </MenuItem>
  );
};

LanguageTranslationMenuItem.propTypes = {
  className: string.isRequired,
  onClickCallback: func.isRequired,
  showGoogleTranslate: bool.isRequired,
  showIcon: bool,
};

LanguageTranslationMenuItem.defaultProps = {
  showIcon: false,
};

export default LanguageTranslationMenuItem;
